/**
 * The native outbox, seen from the app: a second, durable copy of every row
 * that has not reached the server yet (DATA_MODEL §2.3).
 *
 * Why it exists. On a phone the ledger's source of truth is IndexedDB inside a
 * WebView, and a WebView's storage can be reclaimed by the OS. Anything not yet
 * uploaded would go with it. So every dirty write is mirrored into a SQLite
 * file the app owns (`outbox_middleware.ts`), the server's acceptance removes
 * it (`sync.ts`), and at launch this module puts back whatever IndexedDB lost.
 * The widgets and App Intents write into the same file, so a coffee logged from
 * the lock screen arrives in the ledger by exactly the same path.
 *
 * **Never another account's rows.** An entry whose row belongs to a different
 * account is counted and left where it is, never imported — the same rule as
 * the push guard in `sync.ts`, for the same reason: the local database is one
 * un-partitioned set of tables, and importing it would file one person's
 * expenses under another's name on the next push.
 *
 * Framework-free and store-agnostic: the store is the native plugin on a phone
 * and an in-memory one in the tests.
 */

import type { OutboxAck, OutboxEntry, OutboxKey } from "@/native/bridge";

import { familyLedger } from "./ledger";
import { cachedFamily } from "./family";
import { db, familyDb } from "./db";
import { outboxMiddleware } from "./outbox_middleware";
import { remoteTable, setSyncOutbox } from "./sync";
import { SYNCED_TABLES } from "./synced_tables";
import { unknownFields } from "./types";

/** The five operations the native SQLite outbox offers. */
export interface OutboxStore {
  put(entries: readonly OutboxEntry[]): Promise<void>;
  remove(keys: readonly OutboxKey[]): Promise<void>;
  /** Remove each entry only if it is still the version that was accepted. */
  ack(acks: readonly OutboxAck[]): Promise<void>;
  all(): Promise<readonly OutboxEntry[]>;
  clear(): Promise<void>;
}

export interface RefillReport {
  /** Rows put back into the ledger, dirty, to upload. */
  imported: number;
  /** Entries already in the ledger at that version or newer — removed or left for the ack. */
  current: number;
  /** Entries belonging to another account — counted, never imported (INV-3's local half). */
  foreign: number;
  /** Entries that would not parse, or name a table this app does not know — counted (INV-7). */
  unreadable: number;
}

/**
 * Put back into the ledger every outbox row it lacks, for `ownerId`'s account.
 *
 * - The ledger has no copy, or an older one: imported, **dirty**, so the next
 *   sync uploads it. An entry in the outbox has not been acknowledged, so it
 *   cannot be assumed to be on the server.
 * - The ledger already has this version clean: the server has it — the entry
 *   is acknowledged away.
 * - The ledger has it dirty, or newer: left alone; the next sync's ack (or
 *   the middleware's replacement) settles the entry.
 */
export async function importOutboxEntries(
  entries: readonly OutboxEntry[],
  ownerId: string,
  store: OutboxStore,
  nativeFamilyOwner?: string | null,
): Promise<RefillReport> {
  const report: RefillReport = { imported: 0, current: 0, foreign: 0, unreadable: 0 };
  const settled: OutboxAck[] = [];
  let family;
  let familyFailed = false;
  try { family = await cachedFamily(); } catch (cause) {
    familyFailed = true;
    report.unreadable += 1;
    console.warn("Could not read family membership while restoring the outbox:", cause);
  }
  const familyOwner = familyFailed ? undefined : family?.family_id ?? nativeFamilyOwner;

  for (const entry of entries) {
    const spec = remoteTable(entry.table);
    if (spec === null) {
      report.unreadable += 1;
      continue;
    }

    let domain;
    let extra: Record<string, unknown> | undefined;
    try {
      const raw: unknown = JSON.parse(entry.payload);
      domain = spec.fromWire(raw);
      if ((entry.ledger === "family" || spec.ownerOf(domain) === familyOwner) && "splitRef" in domain && domain.splitRef != null) throw new Error("Split shares belong to personal ledgers.");
      extra = unknownFields(raw, domain);
    } catch (cause) {
      // Quarantined and counted, never allowed to stop the rest (INV-7) — and
      // never deleted: it may be the only copy.
      console.warn(
        `Skipped an unreadable outbox entry (${entry.table} ${entry.rowId}):`,
        cause instanceof Error ? cause.message : cause,
      );
      report.unreadable += 1;
      continue;
    }

    if (domain.id !== entry.rowId || domain.updatedAt !== entry.updatedAt) {
      report.unreadable += 1;
      continue;
    }
    if (familyFailed && entry.ledger === "family") continue;
    const rowOwner = spec.ownerOf(domain);
    const target = rowOwner === ownerId ? spec : rowOwner === familyOwner ? remoteTable(entry.table, familyLedger(rowOwner)) : null;
    if (!target) {
      report.foreign += 1;
      continue;
    }

    const local = await target.table().get(entry.rowId);
    if (local !== undefined && local.updatedAt >= entry.updatedAt) {
      report.current += 1;
      if (local.updatedAt === entry.updatedAt && local._dirty === 0) {
        settled.push({ table: entry.table, rowId: entry.rowId, updatedAt: entry.updatedAt, ...(entry.ledger ? { ledger: entry.ledger } : {}) });
      }
      continue;
    }

    await target.table().put({ ...domain, _dirty: 1, _syncedAt: local?._syncedAt ?? null, _extra: extra });
    report.imported += 1;
  }

  if (settled.length > 0) await store.ack(settled);
  return report;
}

/** At launch: everything in the outbox, through `importOutboxEntries`. */
export async function refillFromOutbox(store: OutboxStore, ownerId: string, nativeFamilyOwner?: string | null): Promise<RefillReport> {
  return importOutboxEntries(await store.all(), ownerId, store, nativeFamilyOwner);
}

/** The bridge's outbox methods, as an `OutboxStore`. */
export function outboxStoreFrom(bridge: {
  outboxPut: OutboxStore["put"];
  outboxRemove: OutboxStore["remove"];
  outboxAck: OutboxStore["ack"];
  outboxAll: OutboxStore["all"];
  outboxClear: OutboxStore["clear"];
}): OutboxStore {
  return {
    put: (entries) => bridge.outboxPut(entries),
    remove: (keys) => bridge.outboxRemove(keys),
    ack: (acks) => bridge.outboxAck(acks),
    all: () => bridge.outboxAll(),
    clear: () => bridge.outboxClear(),
  };
}

/**
 * Switch the outbox on: mirror every dirty write into it, and let sync
 * acknowledge what the server accepts. Called once at boot on a phone, before
 * anything can write; never on the web.
 *
 * Dexie builds its middleware stack when the database opens, so a database
 * already open is closed and reopened around the registration — otherwise the
 * mirror would silently not exist.
 */
export async function startNativeOutbox(store: OutboxStore): Promise<void> {
  for (const database of [db, familyDb]) {
    const wasOpen = database.isOpen();
    if (wasOpen) database.close();
    const family = database === familyDb;
    const tables = family ? Object.fromEntries(Object.entries(SYNCED_TABLES).filter(([, remote]) => remoteTable(remote, familyLedger("")) !== null)) : SYNCED_TABLES;
    database.use(outboxMiddleware(store, tables, family ? "family" : undefined));
    if (wasOpen) await database.open();
  }
  setSyncOutbox(store);
}
